import { Component, EventEmitter, Input, Output, OnInit, HostListener, OnChanges, SimpleChanges } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { NewPatientInfo } from '../../card/new-patient-card/new-patient-card.component';

@Component({
  selector: 'app-movement-history-popup',
  templateUrl: './movement-history-popup.component.html',
  styleUrls: ['./movement-history-popup.component.css']
})
export class MovementHistoryPopupComponent implements OnInit, OnChanges {
  @Input() patientId: number | null = null;
  @Input() patientList: NewPatientInfo[] = [];
  @Input() useTestData: boolean = false;
  @Output() close = new EventEmitter<void>();

  showPopup = false;
  loadingHistory = false;
  patientInfo: { patientId: number; patientName: string } | null = null;
  movementHistory: string[] = [];

  private testDataMap: { [key: string]: any } = {};

  constructor(private http: HttpClient) {}

  ngOnInit(): void {
    if (this.patientId) {
      if (this.useTestData) {
        this.loadTestData(() => {
          this.loadMovementHistory(this.patientId!);
        });
      } else {
        this.loadMovementHistory(this.patientId);
      }
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['patientId']) {
      const newPatientId = changes['patientId'].currentValue;
      if (newPatientId) {
        if (this.useTestData && Object.keys(this.testDataMap).length === 0) {
          this.loadTestData(() => {
            this.loadMovementHistory(newPatientId);
          });
        } else {
          this.loadMovementHistory(newPatientId);
        }
      } else {
        this.showPopup = false;
        this.patientInfo = null;
        this.movementHistory = [];
      }
    }
  }

  /**
   * テストデータを読み込む
   */
  loadTestData(callback?: () => void): void {
    this.http.get<any>('/assets/test-data/patient-detail-test-data.json')
      .subscribe(
        (data: any) => {
          this.testDataMap = data;
          if (callback) {
            callback();
          }
        },
        (error: HttpErrorResponse) => {
          // 移動履歴テストデータの読み込みに失敗
          if (callback) {
            callback();
          }
        }
      );
  }

  /**
   * 移動履歴を読み込む（患者詳細APIと同じデータを使用）
   */
  loadMovementHistory(patientId: number): void {
    this.loadingHistory = true;
    this.showPopup = true;

    if (this.useTestData) {
      const testData = this.testDataMap[patientId.toString()];
      if (testData) {
        // 患者詳細のテストデータから移動履歴を取得
        this.patientInfo = {
          patientId: testData.patientId,
          patientName: testData.patientName
        };
        // 患者詳細APIと同じフィールド名を使用
        this.movementHistory = testData.movementHistory || [];
        this.loadingHistory = false;
        return;
      } else {
        // リストから基本情報を使用
        const patient = this.patientList.find(p => p.patientId === patientId);
        if (patient) {
          this.patientInfo = {
            patientId: patient.patientId,
            patientName: patient.patientName
          };
          this.movementHistory = [];
        }
        this.loadingHistory = false;
        return;
      }
    }

    // 実API呼び出し - 患者詳細APIと同じエンドポイントを使用
    this.http.get<any>(`/patient/${patientId}`)
      .subscribe(
        (res: any) => {
          if (res.resultCode === 200 && res.data) {
            this.patientInfo = {
              patientId: res.data.patientId,
              patientName: res.data.patientName
            };
            // 患者詳細APIのmovementHistoryフィールドを使用
            this.movementHistory = res.data.movementHistory || [];
          }
          this.loadingHistory = false;
        },
        (e: HttpErrorResponse) => {
          // 移動履歴APIリクエストが失敗
          this.loadingHistory = false;
          // エラー時はリストから基本情報を使用
          const patient = this.patientList.find(p => p.patientId === patientId);
          if (patient) {
            this.patientInfo = {
              patientId: patient.patientId,
              patientName: patient.patientName
            };
            this.movementHistory = [];
          }
        }
      );
  }

  /**
   * ポップアップを閉じる
   */
  closePopup(): void {
    this.showPopup = false;
    this.patientInfo = null;
    this.movementHistory = [];
    this.close.emit();
  }

  /**
   * ESCキーでポップアップを閉じる
   */
  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKey(event: KeyboardEvent): void {
    if (this.showPopup) {
      this.closePopup();
    }
  }

  /**
   * ポップアップのオーバーレイクリックで閉じる
   */
  onOverlayClick(event: MouseEvent): void {
    if (event.target === event.currentTarget) {
      this.closePopup();
    }
  }

  /**
   * 位置アイコンを取得
   */
  getLocationIcon(location: string): string {
    if (location.includes('廊下')) return 'assets/images/icon/delirium/hallway.svg';
    if (location.includes('トイレ')) return 'assets/images/icon/delirium/toilet.svg';
    if (location.includes('他室')) return 'assets/images/icon/delirium/other_room.svg';
    return 'assets/images/icon/delirium/in_bed.svg';
  }

  /**
   * 位置テキストを取得（移動履歴から）
   */
  getLocationText(historyItem: string): string {
    // "20:45 廊下" から "廊下" を抽出
    const parts = historyItem.split(' ');
    return parts.length > 1 ? parts.slice(1).join(' ') : historyItem;
  }

  /**
   * 時間テキストを取得（移動履歴から）
   */
  getTimeText(historyItem: string): string {
    // "20:45 廊下" から "20:45" を抽出
    const parts = historyItem.split(' ');
    return parts.length > 0 ? parts[0] : historyItem;
  }
}

